import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useActiveFBOs } from "../../../../../containers/ActiveFBOContainer";
import { Ramp } from "../../../../../types";
import { uuidv4 } from "../../../../../utils";
import { Shape } from "../RampCanvas/RampCanvas";
import { GISShapeEditGrid } from "./GISShapeEditGrid";
import { convertGISPolygonToPixels, parseGISCSV } from "./gisUtils";

type Props = {
  open: boolean;
  onClose: () => void;
  onImportShapes: (shapes: Shape[]) => void;
  ramp: Ramp;
};

interface ImportedGISShape {
  id: string;
  label: string;
  originalPolygon: number[][];
  pixelPoints: { x: number; y: number }[];
  tags: string[];
  stroke_style: string;
  fill_style: string;
  font_size: number;
  text: string;
  // Additional optional shape attributes
  type?: string;
  locked?: boolean;
  line_style?: string;
  line_width?: number;
  fill_pattern?: string;
}

export const GISImportDialog: React.FC<Props> = ({
  open,
  onClose,
  onImportShapes,
  ramp,
}) => {
  const { activeFBO } = useActiveFBOs();
  const [csvContent, setCsvContent] = React.useState<string>("");
  const [importedShapes, setImportedShapes] = React.useState<
    ImportedGISShape[]
  >([]);
  const [hasProcessed, setHasProcessed] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      // Reset all state when dialog opens
      setCsvContent("");
      setImportedShapes([]);
      setHasProcessed(false);
      setError("");
    }
  }, [open]);

  const handleProcessCSV = () => {
    try {
      setError("");

      if (!ramp.calibration_points || ramp.calibration_points.length < 2) {
        setError(
          "Ramp must have at least 2 calibration points to import GIS shapes"
        );
        return;
      }

      const parsedData = parseGISCSV(csvContent);

      const shapes: ImportedGISShape[] = parsedData.map((row) => ({
        id: uuidv4(),
        label: row.ramp_label || "Imported Shape",
        originalPolygon: row.polygon,
        pixelPoints: convertGISPolygonToPixels(
          row.polygon,
          ramp.calibration_points,
          ramp.width,
          ramp.depth,
          1000 // Default canvas width used by the ramp editor
        ),
        tags: [],
        stroke_style: row.stroke_style || "#3c6ce9",
        fill_style: row.fill_style || "rgba(60, 108, 233, 0.1)",
        font_size: row.font_size || 14,
        text: row.text || row.ramp_label || "",
        // Optional attributes from CSV
        type: row.type,
        locked: row.locked,
        line_style: row.line_style,
        line_width: row.line_width,
        fill_pattern: row.fill_pattern,
      }));

      setImportedShapes(shapes);
      setHasProcessed(true);
    } catch (err) {
      setError(err.message || "Failed to process CSV data");
    }
  };

  const handleDownloadSample = () => {
    // Provide sample in TSV format (tab-separated) which is common when copying from spreadsheets
    const sampleTSV = `ramp_label	ramp_polygon	type	locked	stroke_style	line_style	line_width	fill_style	fill_pattern	text	font_size
"Hangar A"	"[[-73.70404052734375, 41.06755447387695], [-73.70372772216797, 41.0671501159668], [-73.70323181152344, 41.06735610961914], [-73.70356750488281, 41.0677604675293], [-73.70404052734375, 41.06755447387695]]"	Area	false	#FF0000	solid	2	#FF000033		Hangar A	14
"Parking Zone B"	"[[-73.70435333251953, 41.06752395629883], [-73.70458221435547, 41.06742477416992], [-73.7043228149414, 41.06711959838867], [-73.70410919189453, 41.067222595214844], [-73.70435333251953, 41.06752395629883]]"	Area	true	#00FF00	dashed	3	#00FF004D	diagonal	"Parking B"	16
"Taxiway Line"	"[[-73.70281219482422, 41.06663131713867], [-73.70298767089844, 41.06631088256836], [-73.7027359008789, 41.066226959228516], [-73.70256805419922, 41.06655502319336]]"	Line	false	#0000FF	solid	4	transparent	none	Taxiway	12
"Fuel Station"	"[[-73.70306396484375, 41.066707611083984], [-73.7032470703125, 41.0663948059082], [-73.70298767089844, 41.06631088256836], [-73.70281219482422, 41.06663131713867]]"	Area	false	#FFFF00	dotted	1	#FFFF0026	solid	"Fuel"	18`;

    const blob = new Blob([sampleTSV], {
      type: "text/tab-separated-values;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "gis_shapes_sample.tsv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = () => {
    const shapes: Shape[] = importedShapes.map((gisShape) => ({
      id: gisShape.id,
      fbo_id: ramp.fbo_id,
      ramp_id: ramp.id,
      tags: gisShape.tags,
      points: gisShape.pixelPoints,
      type: (gisShape.type as Shape["type"]) || "Area",
      locked: gisShape.locked ?? false,
      stroke_style: gisShape.stroke_style,
      line_style: (gisShape.line_style as Shape["line_style"]) || "solid",
      line_width: gisShape.line_width ?? 2,
      fill_style: gisShape.fill_style,
      fill_pattern: gisShape.fill_pattern,
      text: gisShape.text,
      font_size: gisShape.font_size,
      reference_image_height: null,
    }));

    onImportShapes(shapes);

    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import GIS Shapes</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={3} sx={{ minHeight: 400 }}>
          {!hasProcessed ? (
            <>
              <Stack direction="column" spacing={2}>
                <Typography variant="subtitle1">
                  CSV Format Requirements
                </Typography>
                <Stack direction="column" spacing={1}>
                  <Typography variant="body2">
                    <strong>Delimiter:</strong> Comma (,) or Tab (\t)
                  </Typography>
                  <Typography variant="body2">
                    <strong>Required columns:</strong> ramp_label, ramp_polygon
                  </Typography>
                  <Typography variant="body2">
                    <strong>Optional styling columns:</strong> type, locked,
                    stroke_style, line_style, line_width, fill_style,
                    fill_pattern, text, font_size
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Download the sample file below to see examples with all
                    supported columns and formatting.
                  </Typography>
                </Stack>
              </Stack>

              <Stack direction="column" spacing={2}>
                <TextField
                  multiline
                  rows={12}
                  fullWidth
                  placeholder={`ramp_label	ramp_polygon	type	stroke_style	fill_style
"Hangar A"	"[[-73.70404052734375, 41.06755447387695], [-73.70372772216797, 41.0671501159668]]"	Area	#FF0000	#FF000033
"Parking Zone B"	"[[-73.70404052734375, 41.06755447387695], [-73.70372772216797, 41.0671501159668]]"	Area	#00FF00	#00FF004D`}
                  value={csvContent}
                  onChange={(e) => setCsvContent(e.target.value)}
                  variant="outlined"
                />
              </Stack>

              {error && (
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              )}
            </>
          ) : (
            <Stack direction="column" spacing={2}>
              <Typography variant="h6">
                Imported Shapes ({importedShapes.length})
              </Typography>
              <GISShapeEditGrid
                shapes={importedShapes}
                setShapes={setImportedShapes}
                ramp={ramp}
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!hasProcessed ? (
          <>
            <Button
              variant="outlined"
              onClick={handleDownloadSample}
              sx={{ mr: 1 }}
            >
              Download Sample TSV
            </Button>
            <Button
              variant="contained"
              onClick={handleProcessCSV}
              disabled={!csvContent.trim()}
            >
              Process CSV
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            color="success"
            onClick={handleImport}
            disabled={importedShapes.length === 0}
          >
            Import {importedShapes.length} Shapes
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
